import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { RefreshCw, AlertCircle, CheckCircle2, Pause, Play, Trash2, Repeat } from 'lucide-react';
import {
  monitoredSiteApi,
  MonitoredSite,
  IntervalType,
} from '@/api/monitoredSiteApi';
import { parseServerTime } from '@/lib/serverTime';

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: '감시 중',
  PAUSED: '일시중지',
  FAILED: '자동 중지(연속 실패)',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  PAUSED: 'bg-gray-100 text-gray-600',
  FAILED: 'bg-red-100 text-red-700',
};

const BULK_URL_LIMIT = 200;

// 백엔드 MonitoredSiteService.RUN_NOW_COOLDOWN_MINUTES와 동일한 값 -
// 서버 재시작 등으로 완료 처리가 누락돼 runningSince가 오래 남아있는 경우까지
// 화면에서 영구히 "실행 중"으로 표시하지 않기 위한 상한.
const RUN_NOW_STALE_MINUTES = 10;

function isRunning(site: MonitoredSite): boolean {
  const runningAt = parseServerTime(site.runningSince);
  if (!runningAt) return false;
  const elapsedMs = Date.now() - runningAt.getTime();
  return elapsedMs < RUN_NOW_STALE_MINUTES * 60 * 1000;
}

export default function MonitoredSitesPage() {
  const { user } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const isSuperAdmin = user?.userRole === 'SUPER_ADMIN';
  const isFreePlan = user?.subscriptionPlan === 'FREE' && !isSuperAdmin;

  const [sites, setSites] = useState<MonitoredSite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [urlsInput, setUrlsInput] = useState('');
  const [intervalType, setIntervalType] = useState<IntervalType>('DAILY');
  const [submitting, setSubmitting] = useState(false);
  const [rowErrors, setRowErrors] = useState<{ url: string; reason: string }[]>([]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // silent=true는 진행 중인 크롤링을 폴링할 때 사용 - 전체화면 스피너로 목록을
  // 가리지 않고 조용히 갱신한다.
  const loadSites = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const result = await monitoredSiteApi.list();
      setSites(result);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || '목록 조회 중 오류가 발생했습니다.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 진행 중인 사이트가 있으면 완료 여부를 주기적으로 확인 - "지금 실행" 버튼이
  // 언제 끝나는지 알 수 없다는 문제를 해결하기 위해 화면이 스스로 갱신된다.
  useEffect(() => {
    if (!sites.some(isRunning)) return;
    const interval = setInterval(() => loadSites(true), 5000);
    return () => clearInterval(interval);
  }, [sites]);

  const parseUrls = () =>
    Array.from(
      new Set(
        urlsInput
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
      )
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isFreePlan) return;

    const urls = parseUrls();
    if (urls.length === 0) {
      showToast('등록할 URL을 입력해주세요.', 'error');
      return;
    }
    if (urls.length > BULK_URL_LIMIT) {
      showToast(`한 번에 등록 가능한 URL은 최대 ${BULK_URL_LIMIT}개입니다. (입력: ${urls.length}개)`, 'error');
      return;
    }

    setSubmitting(true);
    setRowErrors([]);
    try {
      if (urls.length === 1) {
        await monitoredSiteApi.register({ url: urls[0], intervalType });
        showToast('사이트가 등록되었습니다.');
      } else {
        const result = await monitoredSiteApi.registerBulk({ urls, intervalType });
        if (result.failureCount > 0) {
          setRowErrors(result.errors.map((e) => ({ url: e.url, reason: e.reason })));
          showToast(`${result.successCount}건 등록 성공, ${result.failureCount}건 실패 (아래 목록 참고)`, result.successCount > 0 ? 'success' : 'error');
        } else {
          showToast(`${result.successCount}건 모두 등록되었습니다.`);
        }
      }
      setUrlsInput('');
      loadSites();
    } catch (e: any) {
      showToast(e.response?.data?.message || e.message || '등록 중 오류가 발생했습니다.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePauseResume = async (site: MonitoredSite) => {
    try {
      if (site.status === 'PAUSED' || site.status === 'FAILED') {
        await monitoredSiteApi.resume(site.id);
        showToast('감시를 재개했습니다.');
      } else {
        await monitoredSiteApi.pause(site.id);
        showToast('감시를 일시중지했습니다.');
      }
      loadSites();
    } catch (e: any) {
      showToast(e.response?.data?.message || e.message || '처리 중 오류가 발생했습니다.', 'error');
    }
  };

  const handleRunNow = async (site: MonitoredSite) => {
    try {
      const updated = await monitoredSiteApi.runNow(site.id);
      setSites((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      showToast('지금 실행을 시작했습니다. 완료되면 자동으로 반영됩니다.');
    } catch (e: any) {
      showToast(e.response?.data?.message || e.message || '실행 요청 중 오류가 발생했습니다.', 'error');
    }
  };

  const handleDelete = async (site: MonitoredSite) => {
    if (!window.confirm(`이 사이트 감시를 삭제할까요?\n${site.url}`)) return;
    try {
      await monitoredSiteApi.remove(site.id);
      showToast('삭제되었습니다.');
      loadSites();
    } catch (e: any) {
      showToast(e.response?.data?.message || e.message || '삭제 중 오류가 발생했습니다.', 'error');
    }
  };

  return (
    <PageContainer maxWidth="full">
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top">
          <Alert className={toast.type === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}>
            {toast.type === 'success' ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-red-600" />}
            <AlertDescription className={toast.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {toast.message}
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            {menuIcon ? <span className="text-3xl">{menuIcon}</span> : <Repeat className="w-7 h-7 text-primary" />}
            {menuName || '정기 크롤링'}
          </h1>
          <p className="text-gray-600 mt-2">
            사이트를 한 번 등록해두면 주기적으로 재크롤링해서 새로 추가된 이미지만 자동으로 ALT 텍스트를 생성합니다.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard/scheduled-crawl-usage">
            <Button variant="outline">이미지 목록 보기</Button>
          </Link>
          <Button variant="outline" onClick={() => loadSites()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            새로고침
          </Button>
        </div>
      </div>

      {isFreePlan && (
        <Alert className="border-orange-400 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>무료 플랜은 정기 크롤링(자동 감지)을 지원하지 않습니다.</strong>{' '}
            스타터 플랜 이상 회원에게만 제공됩니다.{' '}
            <a href="/dashboard/my/plan" className="underline font-semibold hover:text-orange-900">플랜 업그레이드 바로가기</a>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">사이트 등록</CardTitle>
          <CardDescription>
            한 줄에 URL 하나씩 입력하세요. 여러 개를 한 번에 등록할 수 있습니다(최대 {BULK_URL_LIMIT}개).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <textarea
              value={urlsInput}
              onChange={(e) => setUrlsInput(e.target.value)}
              placeholder={'https://shop.example.com/products\nhttps://shop.example.com/new-arrivals'}
              disabled={isFreePlan || submitting}
              rows={5}
              className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono ring-offset-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="text-sm text-gray-600 block mb-1">재크롤링 주기</label>
                <Select value={intervalType} onValueChange={(v) => setIntervalType(v as IntervalType)} disabled={isFreePlan || submitting}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DAILY">매일</SelectItem>
                    <SelectItem value="WEEKLY">매주</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={isFreePlan || submitting} title={isFreePlan ? '정기 크롤링은 스타터 플랜 이상만 가능합니다' : undefined}>
                {submitting ? '등록 중...' : '등록'}
                {isFreePlan && <span className="ml-1 text-xs text-orange-200">(유료 플랜 전용)</span>}
              </Button>
            </div>
          </form>

          {rowErrors.length > 0 && (
            <div className="mt-4 border border-red-200 rounded-md p-3 bg-red-50">
              <p className="text-sm font-medium text-red-800 mb-2">등록 실패한 URL</p>
              <ul className="text-sm text-red-700 space-y-1">
                {rowErrors.map((e, idx) => (
                  <li key={idx}>
                    <span className="font-mono break-all">{e.url}</span> — {e.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">등록된 사이트 ({sites.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner size="md" />
            </div>
          ) : sites.length === 0 ? (
            <p className="text-center text-gray-400 py-10">등록된 감시 사이트가 없습니다.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>URL</TableHead>
                  <TableHead>주기</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>마지막 크롤링</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sites.map((site) => (
                  <TableRow key={site.id}>
                    <TableCell className="max-w-xs">
                      <span className="font-mono text-sm break-all">{site.url}</span>
                    </TableCell>
                    <TableCell>{site.intervalType === 'DAILY' ? '매일' : '매주'}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_BADGE_CLASS[site.status] || 'bg-gray-100 text-gray-600'}>
                        {STATUS_LABEL[site.status] || site.status}
                      </Badge>
                      {isRunning(site) && (
                        <Badge className="bg-blue-100 text-blue-700 ml-2">
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin inline" />
                          실행 중
                        </Badge>
                      )}
                      {site.consecutiveFailureCount > 0 && (
                        <div className="text-xs text-red-500 mt-1" title={site.lastErrorMessage || undefined}>
                          최근 크롤링 {site.consecutiveFailureCount}회 연속 실패
                          {site.lastErrorMessage && `: ${site.lastErrorMessage}`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {site.lastCrawledAt ? parseServerTime(site.lastCrawledAt)!.toLocaleString('ko-KR') : '아직 없음'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRunNow(site)}
                        disabled={site.status !== 'ACTIVE' || isRunning(site)}
                        title={
                          site.status !== 'ACTIVE'
                            ? '감시 중인 사이트만 지금 실행할 수 있습니다'
                            : isRunning(site)
                            ? '크롤링이 진행 중입니다'
                            : '지금 실행'
                        }
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isRunning(site) ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handlePauseResume(site)}>
                        {site.status === 'ACTIVE' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(site)}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
